import { Ref, inject, markRaw, ref } from 'vue';

export const DYNAMIC_DIALOG_INJECT_SYMBOL = Symbol('dynamic-dialog');

type UndefinedOptional<T> = {
  [K in keyof T as undefined extends T[K] ? never : K]: T[K];
} & {
  [K in keyof T as undefined extends T[K] ? K : never]?: T[K];
};

export type Constructor<Props> = {
  setup?(props: Props, ...rest: any[]): any;
};

export type DynamicDialog = {
  id: string;
  component: Constructor<any>;
  props: any;
  isOpen: Ref<boolean>;
  close: () => void;
};

export type DynamicDialogs = Ref<{
  activeDialogs: DynamicDialog[];
}>;

export type UseDynamicDialogOptions<
  Props,
  RequiredProps = Props extends {
    dialogProps: any;
  }
    ? Omit<Props, 'dialogProps'>
    : Props,
> = {
  component: Constructor<Props>;
  props: UndefinedOptional<RequiredProps>;
  closeDelay?: number;
};

const DEFAULT_DYNAMIC_DIALOG_OPTIONS = {
  closeDelay: 100,
};

export function useDynamicDialogs() {
  const dynamicDialogs = inject<DynamicDialogs>(DYNAMIC_DIALOG_INJECT_SYMBOL);
  if (!dynamicDialogs) {
    throw new Error(
      'Using useDynamicDialogs/DynamicDialogManager requires DynamicDialogPlugin to be installed',
    );
  }
  return dynamicDialogs;
}

export function useDynamicDialog<
  Props extends Record<string, unknown>,
  DialogProps = Props extends {
    dialogProps: any;
  }
    ? Props['dialogProps']
    : void,
>(opts: UseDynamicDialogOptions<Props>) {
  const options = { ...DEFAULT_DYNAMIC_DIALOG_OPTIONS, ...opts };
  const dynamicDialogs = useDynamicDialogs();
  const id = Math.random().toString(36).substring(7);

  // If the Props has the 'dialogProps' property, this needs to be passed to the component, otherwise undefined
  function open(dialogProps: DialogProps) {
    const dialog = dynamicDialogs.value.activeDialogs.find(
      (dialog) => dialog.id === id,
    );
    if (dialog && dialog.isOpen.value) {
      // If the dialog is already open, we don't want to open it again
      return;
    }

    dynamicDialogs.value.activeDialogs.push(
      markRaw({
        id,
        isOpen: ref(true),
        component: options.component,
        props: {
          ...options.props,
          dialogProps,
        },
        close: () => close(),
      }),
    );
  }

  async function close() {
    // Close dialog by setting open to false
    const dialog = dynamicDialogs.value.activeDialogs.find(
      (dialog) => dialog.id === id,
    );
    if (dialog) {
      dialog.isOpen.value = false;

      // Wait 100ms to allow the dialog to close before removing it from the DOM
      await new Promise<void>((resolve) =>
        setTimeout(() => {
          dynamicDialogs.value.activeDialogs =
            dynamicDialogs.value.activeDialogs.filter(
              (d) => d.id !== dialog.id,
            );
          resolve();
        }, options.closeDelay),
      );
    }
  }

  return {
    open,
    close,
  };
}
